import { useCallback, useEffect, useState } from 'react';
import styles from './GlobalMessages.module.css';
import { getProjectMessages, dismissProjectMessage } from '../../utils/api_manager';
import MessageTile from './MessageTile';

/**
 * Project-wide global messages banner, mounted once at the AppLayout
 * level (TRD: project-global-messages-trd.md, Section 4, Bounded Unit
 * B, Requirement 1). Fetches GET /project/:projectId/messages on
 * mount and on currentProject change (identical fetch-on-mount-and-
 * project-change pattern to InsightsPage.jsx), and renders one
 * MessageTile per message in server response order (no client re-sort --
 * TRD 2.1).
 *
 * Renders `null` -- not just an empty state, an actually absent DOM node --
 * in all three of: before the first fetch resolves, an empty result, and a
 * fetch failure. PRD Section 7 requires fail-silent behavior on error: no
 * banner, no retry UI, just a console.error and nothing rendered (mirrors
 * InsightsPage.jsx's own console.error-on-fetch-failure convention).
 */
const GlobalMessages = ({ currentProject }) => {
  // null = not yet fetched; [] = fetched, zero active messages; array =
  // ready. All three collapse to the same `return null` below -- this
  // state split exists only to mirror InsightsPage.jsx's own convention,
  // not because any branch renders differently here.
  const [messages, setMessages] = useState(null);

  // Display-cap expand/collapse (TRD: global-access-messages-consolidation,
  // Section 5, Requirement 1). Pure client-side display state -- no API
  // call, no interaction with handleDismiss's optimistic-update logic
  // below. Default false: only the first 3 messages (server response
  // order, unchanged) are visible until the user opts to expand.
  const [isExpanded, setIsExpanded] = useState(false);

  const fetchMessages = useCallback((projectId) => {
    return getProjectMessages(projectId)
      .then((response) => {
        if (response.success && response.data) {
          setMessages(response.data.messages || []);
        } else {
          throw new Error(response.message || 'Failed to load project messages');
        }
      })
      .catch((err) => {
        console.error('Error fetching project messages:', err);
        setMessages([]);
      });
  }, []);

  // Fetch on mount and on project change (Requirement 1 / Acceptance
  // Criterion 9). No project context yet -> stay null -> render nothing.
  useEffect(() => {
    if (!currentProject || !currentProject.id) {
      return;
    }
    setMessages(null);
    fetchMessages(currentProject.id);
  }, [currentProject, fetchMessages]);

  // Requirement 6: optimistic removal, no full refetch. Reverts + logs on
  // failure -- mirrors InsightsPage.jsx's handleDismiss exactly.
  const handleDismiss = useCallback(
    (messageId) => {
      if (!currentProject || !currentProject.id) return;

      let removed = null;
      let removedIndex = -1;
      setMessages((prev) => {
        const list = prev || [];
        removedIndex = list.findIndex((m) => m.id === messageId);
        if (removedIndex === -1) return prev;
        removed = list[removedIndex];
        return [...list.slice(0, removedIndex), ...list.slice(removedIndex + 1)];
      });

      const revert = () => {
        if (!removed) return;
        setMessages((prev) => {
          const list = prev || [];
          const next = [...list];
          next.splice(removedIndex, 0, removed);
          return next;
        });
      };

      dismissProjectMessage(currentProject.id, messageId)
        .then((response) => {
          if (!response.success) revert();
        })
        .catch((err) => {
          console.error('Error dismissing project message:', err);
          revert();
        });
    },
    [currentProject]
  );

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  if (!messages || messages.length === 0) {
    return null;
  }

  // 3-message display cap (Requirement 1). Count is always relative to the
  // 3-tile baseline, unchanged by expand state -- "Show N more"/"Hide N"
  // both use the same N. No client re-sort: messages is already in server
  // response order (Requirement 4 / TRD Section 2.6).
  const visibleMessages = messages.slice(0, isExpanded ? messages.length : 3);
  const hiddenCount = messages.length - 3;

  return (
    <div className={styles.container} data-testid="global-messages">
      {visibleMessages.map((message) => (
        <MessageTile
          key={message.id}
          message={message}
          onDismissClick={handleDismiss}
          currentProjectId={currentProject?.id}
        />
      ))}
      {messages.length > 3 && (
        <button
          type="button"
          className={styles.toggleMessagesButton}
          onClick={handleToggleExpand}
          data-testid="global-messages-toggle"
        >
          {isExpanded ? `Hide ${hiddenCount} messages` : `Show ${hiddenCount} more messages`}
        </button>
      )}
    </div>
  );
};

export default GlobalMessages;
